import React from 'react';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form';
// Types
import {
  ExtTalentLinkStatus,
  ExtTalentSocialPlatform,
} from 'api/graphql-global-types';
// Helpers
import {
  ExternalTalentFormValues,
  getExternalTalentSocialHandle,
} from 'helpers/externalTalents';
// Components
import SectionHeader from '../SectionHeader/SectionHeader';
import EnumSelect from 'components/common/EnumSelect/EnumSelect';

const { Text } = Typography;

type SocialsSectionProps = {
  form: FormInstance<ExternalTalentFormValues>;
};

const SocialsSection = ({ form }: SocialsSectionProps): JSX.Element => {
  return (
    <>
      <Divider />

      <SectionHeader
        actionIcon={<PlusOutlined />}
        actionLabel="Add social"
        onAction={() =>
          form.setFieldsValue({
            socials: [
              ...(form.getFieldValue('socials') || []),
              {
                followersCount: null,
                platform: undefined,
                profileName: '',
                status: ExtTalentLinkStatus.active,
                url: '',
              },
            ],
          })
        }
        title="Socials"
      />

      <Text type="secondary">
        Existing social URL and platform are read-only because the current API
        only updates profile name, followers count, and status.
      </Text>

      <Form.List name="socials">
        {(fields, { remove }) => (
          <>
            {fields.map((field) => {
              const socialId = form.getFieldValue([
                'socials',
                field.name,
                'id',
              ]);
              const isExistingSocial = Boolean(socialId);

              return (
                <div key={field.key}>
                  <Divider />

                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item
                        label="Platform"
                        name={[field.name, 'platform']}
                        rules={[
                          {
                            message: 'Select platform',
                            required: true,
                          },
                        ]}
                      >
                        <EnumSelect
                          allowClear
                          disabled={isExistingSocial}
                          options={Object.values(ExtTalentSocialPlatform)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        dependencies={[['socials', field.name, 'platform']]}
                        label="Social URL"
                        name={[field.name, 'url']}
                        rules={[
                          {
                            message: 'Enter social URL',
                            required: true,
                          },
                          {
                            validator(_, value) {
                              const platform = form.getFieldValue([
                                'socials',
                                field.name,
                                'platform',
                              ]);

                              if (
                                !value ||
                                getExternalTalentSocialHandle(value, platform)
                              ) {
                                return Promise.resolve();
                              }

                              return Promise.reject(
                                new Error('Enter a valid social URL or handle')
                              );
                            },
                          },
                        ]}
                      >
                        <Input disabled={isExistingSocial} />
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        label="Profile name"
                        name={[field.name, 'profileName']}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col span={4}>
                      <Form.Item
                        label="Followers"
                        name={[field.name, 'followersCount']}
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>

                    <Col span={2}>
                      <Form.Item label=" " style={{ marginBottom: 0 }}>
                        {!isExistingSocial && (
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item label="Status" name={[field.name, 'status']}>
                        <EnumSelect
                          allowClear
                          options={Object.values(ExtTalentLinkStatus)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              );
            })}
          </>
        )}
      </Form.List>
    </>
  );
};

export default SocialsSection;
